import * as React from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { AdminPage } from "./components/AdminPage";
import { BuyPage } from "./components/BuyPage";
import { ComprarLicencias } from "./components/ComprarLicencias";
import { FakeEmailPage } from "./components/FakeEmailPage";
import { Faq } from "./components/Faq";
import { InstallPage } from "./components/InstallPage";
import { MarketingHome } from "./components/MarketingHome";
import { MyLabelsPage, ViewLabelPage } from "./components/MyLabelsPage";
import { PortalDeDistribuidores } from "./components/PortalDeDistribuidores";
import { EulaPage, PrivacyPage } from "./components/PrivacyPage";
import { SettingsPage } from "./components/SettingsPage";
import { SignInPage } from "./components/SignInPage";
import { AppLoginPage } from "./components/AppLoginPage";
import { TutorialsPage } from "./components/TutorialsPage";
import { UserInfoPage } from "./components/UserInfoPage";
import { ViewDistributorsPage } from "./components/ViewDistributorsPage";
import { WebAppHome } from "./components/WebAppHome";
import {
  RequireAdmin,
  RequireAuth,
  consumeReturnPath,
} from "./shared/authGuards";
import { AnalyticsRouteListener } from "./shared/logging/AnalyticsRouteListener";
import { MARKETING_PATHS, applyPageSeo } from "./shared/seo";

const PAGE_SEO: Record<
  string,
  { title: string; description: string; canonicalPath: string }
> = {
  "/": {
    title: "DinLabel — Professional label design software",
    description:
      "DinLabel is professional label design software for barcodes, QR codes, variable data, and printing.",
    canonicalPath: "/",
  },
  "/install": {
    title: "Install DinLabel | DinLabel",
    description:
      "Download and install DinLabel label design software for Windows. Try the demo or get the full version.",
    canonicalPath: "/install",
  },
  "/buy": {
    title: "Buy DinLabel | DinLabel",
    description:
      "Purchase a DinLabel license online. Professional label design with barcodes, QR codes, and variable data.",
    canonicalPath: "/buy",
  },
  "/tutorials": {
    title: "Tutorials | DinLabel",
    description:
      "Watch DinLabel tutorials for printing labels, barcodes, QR codes, shapes, images, and more.",
    canonicalPath: "/tutorials",
  },
  "/faq": {
    title: "FAQ | DinLabel",
    description:
      "Frequently asked questions about becoming a DinLabel distributor, licenses, pricing, and support.",
    canonicalPath: "/faq",
  },
  "/viewdistributors": {
    title: "Distributors | DinLabel",
    description:
      "Find authorized DinLabel distributors near you.",
    canonicalPath: "/viewdistributors",
  },
  "/privacy": {
    title: "Privacy Policy | DinLabel",
    description: "DinLabel privacy policy and terms of service.",
    canonicalPath: "/privacy",
  },
  "/eula": {
    title: "End User License Agreement | DinLabel",
    description: "DinLabel end user license agreement (EULA).",
    canonicalPath: "/eula",
  },
};

function ReturnPathRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    // OAuth returns to "/". Only restore the saved path on home.
    if (location.pathname !== "/") {
      return;
    }
    const path = consumeReturnPath();
    if (path && path !== "/") {
      navigate(path.startsWith("/") ? path : "/" + path, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

function RouteSeo() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const page = PAGE_SEO[pathname];
    if (page) {
      applyPageSeo({
        title: page.title,
        description: page.description,
        canonicalPath: page.canonicalPath,
        noindex: false,
      });
      return;
    }

    if (MARKETING_PATHS.has(pathname)) {
      applyPageSeo({
        title: "DinLabel",
        description:
          "DinLabel is professional label design software for barcodes, QR codes, variable data, and printing.",
        canonicalPath: pathname,
        noindex: false,
      });
      return;
    }

    applyPageSeo({
      title: "DinLabel",
      description:
        "DinLabel is professional label design software for barcodes, QR codes, variable data, and printing.",
      noindex: true,
    });
  }, [pathname]);

  return null;
}

export function App() {
  return (
    <>
      <ReturnPathRedirect />
      <RouteSeo />
      <AnalyticsRouteListener />
      <Routes>
        <Route path="/" element={<MarketingHome />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/tutorials" element={<TutorialsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/eula" element={<EulaPage />} />
        <Route path="/viewdistributors" element={<ViewDistributorsPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/applogin" element={<AppLoginPage />} />

        <Route path="/buy" element={<BuyPage />} />
        <Route
          path="/userinfo"
          element={
            <RequireAuth>
              <UserInfoPage />
            </RequireAuth>
          }
        />
        <Route
          path="/mylabels"
          element={
            <RequireAuth>
              <MyLabelsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/mylabels/view"
          element={
            <RequireAuth>
              <ViewLabelPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/fakeemail"
          element={
            <RequireAdmin>
              <FakeEmailPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/distributorportal"
          element={
            <RequireAuth>
              <PortalDeDistribuidores />
            </RequireAuth>
          }
        />
        <Route
          path="/account"
          element={
            <RequireAuth>
              <WebAppHome />
            </RequireAuth>
          }
        />
        <Route path="/faq" element={<Faq />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/webapp/faq" element={<Navigate to="/faq" replace />} />
        <Route path="/getDinLabel" element={<Navigate to="/buy" replace />} />
        <Route
          path="/webapp/getDinLabel"
          element={<Navigate to="/buy" replace />}
        />
        <Route
          path="/VerDetallesDeLicencias"
          element={<Navigate to="/distributorportal" replace />}
        />
        <Route
          path="/verDetallesDeLicencias"
          element={<Navigate to="/distributorportal" replace />}
        />
        <Route
          path="/ComprarLicencias"
          element={
            <RequireAuth>
              <ComprarLicencias />
            </RequireAuth>
          }
        />
        <Route
          path="/comprarLicencias"
          element={<Navigate to="/ComprarLicencias" replace />}
        />
        <Route
          path="/tutoriales"
          element={<Navigate to="/distributorportal?focus=tutoriales" replace />}
        />
        <Route path="/webapp" element={<Navigate to="/distributorportal" replace />} />
      </Routes>
    </>
  );
}
